import type {
  Submission, SubmissionResult, ExtractedPDF, BDRSummary,
} from "@shared/types";

const API_BASE = "/api";

class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

async function post<T>(path: string, body: unknown, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...opts,
  });
  if (!res.ok) {
    const details = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (details as any).error ?? res.statusText, details);
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.json() as Promise<T>;
}

export const api = {
  listBdrs: () => get<{ bdrs: BDRSummary[] }>("/bdrs"),

  presignUpload: (bdr_id: string, filename: string, contentType: string) =>
    post<{ url: string; r2_key: string }>("/pdf/presign", { bdr_id, filename, contentType }),

  extractPDF: (bdr_id: string, r2_key: string) =>
    post<ExtractedPDF>("/pdf/extract", { bdr_id, r2_key }),

  submit: (submission: Submission) =>
    post<SubmissionResult>("/submission", { submission }),

  listAdminSubmissions: () => get<{ submissions: any[] }>("/admin/submissions"),

  leaderboard: () => get<LeaderboardData>("/leaderboard"),
};

export interface LeaderboardEntry {
  bdr_id: string;
  bdr_name: string;
  photo_url?: string | null;
  count: number;
}

export interface LeaderboardData {
  week: LeaderboardEntry[];
  month: LeaderboardEntry[];
  all_time: LeaderboardEntry[];
  streaks: Record<string, number>;
}

export { ApiError };
